import { toAppError } from './errors';

/**
 * Georgian labels for the Auth failures the app can surface — sign-in and password setup.
 * Same shape as policyApi's POLICY_ERROR_LABELS: match on the machine-readable code or the
 * stable English fragment, never render the raw text.
 */
const AUTH_ERROR_LABELS: Record<string, string> = {
  // ── sign-in ────────────────────────────────────────────────────────────────────────────────
  invalid_credentials: 'ელფოსტა ან პაროლი არასწორია.',
  'Invalid login credentials': 'ელფოსტა ან პაროლი არასწორია.',
  email_not_confirmed: 'ელფოსტა დადასტურებული არ არის. გახსენით მოწვევის ბმული.',
  'Email not confirmed': 'ელფოსტა დადასტურებული არ არის. გახსენით მოწვევის ბმული.',
  user_banned: 'ანგარიში დროებით დაბლოკილია. მიმართეთ ადმინისტრატორს.',
  // ── password setup ─────────────────────────────────────────────────────────────────────────
  weak_password: 'პაროლი ძალიან სუსტია. აირჩიეთ გრძელი და ნაკლებად სავარაუდო პაროლი.',
  same_password: 'ახალი პაროლი ძველისგან უნდა განსხვავდებოდეს.',
  'New password should be different': 'ახალი პაროლი ძველისგან უნდა განსხვავდებოდეს.',
  'Password should be at least': 'პაროლი ძალიან მოკლეა.',
  // ── link / session lifetime ────────────────────────────────────────────────────────────────
  session_not_found: 'ბმულს ვადა გაუვიდა. მოითხოვეთ ახალი მოწვევა.',
  session_expired: 'ბმულს ვადა გაუვიდა. მოითხოვეთ ახალი მოწვევა.',
  'Auth session missing': 'სესია აღარ არის აქტიური. გახსენით მოწვევის ბმული ხელახლა.',
  'Unable to validate email address': 'ბმული არასწორია. მოითხოვეთ ახალი მოწვევა.',
  'Token has expired': 'ბმულს ვადა გაუვიდა. მოითხოვეთ ახალი მოწვევა.',
  // ── throttling ─────────────────────────────────────────────────────────────────────────────
  over_request_rate_limit: 'ძალიან ბევრი მცდელობა. სცადეთ ცოტა ხანში.',
  over_email_send_rate_limit: 'ძალიან ბევრი მცდელობა. სცადეთ ცოტა ხანში.',
};

/** Neutral enough for any Auth surface; a caller with a better sentence passes its own. */
const AUTH_FALLBACK = 'ოპერაცია ვერ შესრულდა. სცადეთ ხელახლა.';

/**
 * Always returns Georgian. Unlike `policyErrorMessage`, the fallback is a fixed sentence rather
 * than `err.message` — an Auth failure's raw text is English and may quote the address or the
 * token, so it is never surfaced.
 *
 * `fallback` is per-surface on purpose: an unmapped failure while SAVING a password and one
 * while SIGNING IN are different sentences, and a single shared fallback would state the wrong
 * action on one of them.
 */
export function authErrorMessage(e: unknown, fallback: string = AUTH_FALLBACK): string {
  const err = toAppError(e);
  const haystack = `${err.code ?? ''} ${err.message}`;
  for (const [needle, label] of Object.entries(AUTH_ERROR_LABELS)) {
    if (haystack.includes(needle)) return label;
  }
  return fallback;
}
